import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, PlayCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const EndDayCard = ({ summary, onChange }) => {
  const [busy, setBusy] = useState(false);
  const stopped = !!summary?.day_stopped;
  const auto = summary?.day_stop_reason === "auto";

  const toggle = async () => {
    if (!stopped && !window.confirm("End your trading day? No new trades until you resume.")) return;
    setBusy(true);
    try {
      await api.post("/me/end-day", { active: !stopped });
      toast[stopped ? "success" : "error"](
        stopped ? "Trading day resumed" : "Your trading day is closed — no new trades"
      );
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="end-day-card"
      className={`border p-4 flex flex-col gap-3 ${
        auto
          ? "border-red-800 bg-red-500/10"
          : stopped
          ? "border-amber-800 bg-amber-500/10"
          : "border-zinc-800 bg-zinc-900/30"
      }`}
    >
      <div className="flex items-center gap-2">
        {stopped ? (
          <PlayCircle className={`h-4 w-4 ${auto ? "text-red-400" : "text-amber-400"}`} />
        ) : (
          <CalendarCheck className="h-4 w-4 text-emerald-500" />
        )}
        <h3 className="text-sm font-heading font-semibold tracking-tight">
          {auto ? "Day Closed · Loss Limit" : stopped ? "Day Closed" : "My Kill Switch"}
        </h3>
      </div>
      <p className="text-[11px] text-zinc-400 leading-relaxed" data-testid="end-day-note">
        {auto
          ? "You hit your daily loss limit, so your day was closed automatically. Open positions keep their SL and TP; trading reopens tomorrow (IST)."
          : stopped
          ? "You closed your day. Open positions keep running with their SL and TP, but no new trades can be opened until you resume."
          : "Stop yourself for the rest of the day. Open positions keep their SL and TP; only new entries are blocked."}
      </p>
      <button
        data-testid="end-day-btn"
        onClick={toggle}
        disabled={busy || auto}
        className={`w-full py-2.5 rounded-sm text-xs font-bold uppercase tracking-[0.14em] transition-colors duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
          stopped
            ? "border border-emerald-600 text-emerald-400 hover:bg-emerald-600 hover:text-white"
            : "border-2 border-red-700 bg-red-950 text-red-300 hover:bg-red-600 hover:text-white"
        }`}
      >
        {auto ? "Locked until tomorrow" : stopped ? "Resume my day" : "End my day"}
      </button>
    </div>
  );
};
